import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articlesOpen, setArticlesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const catDropdownRef = useRef<HTMLDivElement>(null);
  const artDropdownRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === '/';

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (catDropdownRef.current && !catDropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
      if (artDropdownRef.current && !artDropdownRef.current.contains(event.target as Node)) {
        setArticlesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white shadow-sm border-b border-background-200/80">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 cursor-pointer">
            <span className="font-heading text-xl font-semibold text-foreground-900">
              Basetao<span className="text-primary-500">Sheet</span>
            </span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            <button
              onClick={() => navigate('/')}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer ${isHome ? 'text-primary-600 bg-primary-50' : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'}`}
            >
              Home
            </button>

            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors duration-200 whitespace-nowrap">
              Spreadsheet
            </a>

            <div className="relative" ref={catDropdownRef}>
              <button
                onClick={() => { setIsOpen(!isOpen); setArticlesOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150"
                    >
                      <span className="w-6 h-6 rounded bg-background-100 flex items-center justify-center text-xs font-medium text-foreground-500 flex-shrink-0">
                        {cat.name.charAt(0)}
                      </span>
                      <span className="truncate">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={artDropdownRef}>
              <button
                onClick={() => { setArticlesOpen(!articlesOpen); setIsOpen(false); }}
                className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 hover:bg-background-100 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                Shopping Guide
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${articlesOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {articlesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg border border-background-200/70 shadow-lg py-2 max-h-80 overflow-y-auto">
                  <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Articles</div>
                  {articles.slice(0, 5).map((art) => (
                    <button
                      key={art.slug}
                      onClick={() => { navigate(art.path); setArticlesOpen(false); }}
                      className="block w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 truncate cursor-pointer"
                    >
                      {art.title}
                    </button>
                  ))}
                  <div className="border-t border-background-200/50 mt-1 pt-1">
                    {articles.slice(5).map((art) => (
                      <button
                        key={art.slug}
                        onClick={() => { navigate(art.path); setArticlesOpen(false); }}
                        className="block w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 truncate cursor-pointer"
                      >
                        {art.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="ml-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Explore Guide
            </a>
          </div>

          <button
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer rounded-md hover:bg-background-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl text-foreground-700 ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
            <button
              onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
              className={`text-left px-4 py-3 text-sm font-medium rounded-md transition-colors ${isHome ? 'text-primary-600 bg-primary-50' : 'text-foreground-600 hover:bg-background-100'}`}
            >
              <i className="ri-home-line mr-2"></i>Home
            </button>
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="flex items-center px-4 py-3 text-sm text-foreground-600 hover:bg-background-100 rounded-md transition-colors">
              <i className="ri-file-list-line mr-2"></i>Spreadsheet
            </a>

            <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider mt-2">Categories</div>
            <div className="grid grid-cols-1 gap-1 px-2">
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-4 py-3 text-sm text-foreground-600 hover:bg-background-100 rounded-md transition-colors"
                >
                  <span className="w-7 h-7 rounded bg-background-100 flex items-center justify-center text-xs font-medium text-foreground-500 flex-shrink-0">
                    {cat.name.charAt(0)}
                  </span>
                  <span className="truncate">{cat.name}</span>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-300 text-xs"></i>
                </a>
              ))}
            </div>

            <div className="px-4 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider mt-3">Shopping Guide</div>
            {articles.map((art) => (
              <button
                key={art.slug}
                onClick={() => { navigate(art.path); setMobileMenuOpen(false); }}
                className="flex items-center px-4 py-3 text-sm text-foreground-600 hover:bg-background-100 rounded-md transition-colors text-left cursor-pointer"
              >
                <i className="ri-article-line mr-2 text-foreground-400"></i>
                <span className="truncate">{art.title}</span>
              </button>
            ))}

            <div className="px-4 pt-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full px-5 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-all duration-200 text-center whitespace-nowrap"
              >
                Explore Guide
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}